import { addDays, DAY_MS, type DayKey, pad2, utcMidnight, weekStartKey } from '@k2/core/util'
import { DEFAULT_SCHEDULE_TIME } from '@k2/domain/deliverable-schedule'
import { instantOfWallClock, localDateIn, localTimeIn, zonedTime, zonedWallClock } from '@k2/domain/zoned-time'

export type CalendarDay = {
  key: DayKey
  start: string
  weekend: boolean
}

export type CalendarSlot = { day: DayKey; hour?: number }

export type CalendarWeek = {
  isoWeek: number
  days: CalendarDay[]
  from: string
  to: string
}

export const zonedDayKey = (instant: Date | string, timeZone: string) => {
  const { year, month, day } = localDateIn(new Date(instant), timeZone)
  return `${year}-${pad2(month)}-${pad2(day)}`
}

export const zonedMinutes = (instant: Date | string, timeZone: string) => {
  const { hour, minute } = localTimeIn(new Date(instant), timeZone)
  return hour * 60 + minute
}

// A day key is a wall clock, so the zone resolves it — including across a DST boundary (`@k2/domain/zoned-time`).
export const instantAt = (key: DayKey, minutes: number, timeZone: string) => {
  const [year, month, day] = key.split('-').map(Number) as [number, number, number]
  return zonedTime({ year, month, day }, Math.floor(minutes / 60), minutes % 60, timeZone).toISOString()
}

export const isoWeek = (key: DayKey) => {
  const thursday = utcMidnight(weekStartKey(key)) + 3 * DAY_MS
  const firstOfYear = Date.UTC(new Date(thursday).getUTCFullYear(), 0, 1)
  return Math.floor((thursday - firstOfYear) / DAY_MS / 7) + 1
}

export const calendarWeek = (key: DayKey, timeZone: string) => {
  const monday = weekStartKey(key)
  const days = Array.from({ length: 7 }, (_, index) => {
    const dayKey = addDays(monday, index)
    return { key: dayKey, start: instantAt(dayKey, 0, timeZone), weekend: index > 4 }
  })
  return {
    isoWeek: isoWeek(monday),
    days,
    from: instantAt(monday, 0, timeZone),
    to: instantAt(addDays(monday, 7), 0, timeZone)
  } satisfies CalendarWeek
}

export const shiftWeek = (key: DayKey, weeks: number) => addDays(weekStartKey(key), weeks * 7)

const slotTime = (slot: CalendarSlot, scheduledAt: string | null, timeZone: string) => {
  if (slot.hour !== undefined) return `${pad2(slot.hour)}:00`
  return scheduledAt ? zonedWallClock(scheduledAt, timeZone).slice(11) : DEFAULT_SCHEDULE_TIME
}

export const rescheduledInstant = (slot: CalendarSlot, scheduledAt: string | null, timeZone: string) =>
  instantOfWallClock(`${slot.day}T${slotTime(slot, scheduledAt, timeZone)}`, timeZone)
